import { createFileRoute } from "@tanstack/react-router";

export const Route = createFileRoute("/admin/_layout/organization/branches")({
  component: BranchDataTable,
});

import { useState, useEffect, useMemo } from "react";
import { toast } from "sonner";

import { SidebarInset } from "@/components/ui/sidebar";
import { DashboardHeader } from "@/components/dashboard-header";

import { PaginationComponent } from "@/components/crud/Pagination/Pagination";
import { PAGE_SIZE_OPTIONS } from "@/lib/constants";
import { usePaginationState } from "@/hooks/ui/usePaginationState";
import { DeleteConfirmDialog } from "@/components/crud/DeleteConfirmDialog/DeleteConfirmDialog";
import { BranchFormSheet } from "@/components/crud/forms/BranchFormSheet";

// Hooks
import useFetchBranches, {
  type BranchOrdering,
} from "@/hooks/api/organization/organization-branches/useFetchBranches";
import useCreateBranch from "@/hooks/api/organization/organization-branches/useCreateBranch";
import useUpdateBranch from "@/hooks/api/organization/organization-branches/useUpdateBranch";
import useDeleteBranch from "@/hooks/api/organization/organization-branches/useDeleteBranch";

import { BranchTable } from "@/components/crud/table/BranchTable";
import type { Branch, BranchPayload } from "@/types/organization/branch";
import PageHeader from "@/components/reusable-components/header/page-header";
import FiltersPanel from "@/components/reusable-components/header/filter-pannel";
import { useDebounce } from "@/hooks/use-debounce";
import { useDataTableOrdering } from "@/hooks/ui/useDataTableOrdering";
import type { SortDirection } from "@/components/reusable-components/sort-arrow";
import { SelectFilter } from "@/components/crud/filters/SelectFilter";
import { Input } from "@/components/ui/input";
import { Search } from "lucide-react";
import { useMyPermissions } from "@/hooks/api/authorization/useAuthorization";
import { can } from "@/lib/permissions";

function BranchDataTable() {
  const { data: capabilities } = useMyPermissions();
  const canManage = can(capabilities, "organization.structure.manage");
  // UI State
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [editingBranch, setEditingBranch] = useState<Branch | null>(null);
  const [branchToDelete, setBranchToDelete] = useState<Branch | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isViewOnly, setIsViewOnly] = useState(false);

  const { ordering, toggleOrdering, getApiOrdering, setOrdering } =
    useDataTableOrdering<
      {
        name: SortDirection;
        created_at: SortDirection;
      },
      BranchOrdering
    >({
      name: null,
      created_at: null,
    });

  const [activeFilter, setActiveFilter] = useState<
    "true" | "false" | "clear" | undefined
  >();

  // Global Search Filter
  const [globalFilter, setGlobalFilter] = useState("");
  const [debouncedSearch, resetDebounce] = useDebounce(globalFilter, 500);

  const { page, setPage, pageSize, handlePageSizeChange } =
    usePaginationState();

  // Branch Crud Hooks
  const branchesQuery = useFetchBranches({
    page_size: pageSize,
    page,
    search: debouncedSearch || undefined,
    ordering: getApiOrdering(),
    is_active:
      activeFilter === "true"
        ? true
        : activeFilter === "false"
          ? false
          : undefined,
  });
  const branches = branchesQuery.data?.results ?? [];
  const count = branchesQuery.data?.count ?? 0;

  const createBranch = useCreateBranch();
  const updateBranch = useUpdateBranch();
  const deleteBranch = useDeleteBranch();

  const isMutating =
    createBranch.isPending || updateBranch.isPending || deleteBranch.isPending;
  const isLoading = branchesQuery.isPending || isMutating;
  const isFetching = branchesQuery.isFetching || isMutating;

  // Back to page 1 whenever a filter changes
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, ordering, activeFilter, setPage]);

  // For resetting filters
  const resetFilters = () => {
    setOrdering({
      name: null,
      created_at: null,
    });
    setActiveFilter(undefined);
    setGlobalFilter("");
    resetDebounce();
    setPage(1);
  };

  // Handlers
  const handleCreate = () => {
    setEditingBranch(null);
    setIsViewOnly(false);
    setIsSheetOpen(true);
  };

  const handleEdit = (branch: Branch) => {
    setEditingBranch(branch);
    setIsViewOnly(false);
    setIsSheetOpen(true);
  };

  const handleView = (branch: Branch) => {
    setEditingBranch(branch);
    setIsViewOnly(true);
    setIsSheetOpen(true);
  };

  const handleDeleteClick = (branch: Branch) => {
    setBranchToDelete(branch);
    setIsDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!branchToDelete) return;
    try {
      await deleteBranch.mutateAsync({ id: branchToDelete.id });
      toast.success("Branch deleted successfully!");
      setIsDeleteDialogOpen(false);
      setBranchToDelete(null);
    } catch {
      // error toast comes from the mutation's meta.errorMessage
    }
  };

  const handleFormSubmit = async (data: BranchPayload) => {
    if (editingBranch) {
      await updateBranch.mutateAsync({ id: editingBranch.id, payload: data });
      toast.success("Branch updated successfully!");
    } else {
      await createBranch.mutateAsync(data);
      toast.success("Branch created successfully!");
    }

    setIsSheetOpen(false);
  };

  const totalPages = useMemo(() => {
    return count ? Math.ceil(count / pageSize) : 1;
  }, [count, pageSize]);

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  const [showFilters, setShowFilters] = useState(false);

  return (
    <SidebarInset>
      <DashboardHeader />

      <div className="space-y-4 p-6 pt-0">
        <PageHeader
          title="Branches"
          description="Manage organization branches here."
          onToggleFilter={() => setShowFilters((prev) => !prev)}
          onAdd={canManage ? handleCreate : undefined}
          addLabel="Create Branch"
          isLoading={isFetching}
        />

        <FiltersPanel
          show={showFilters}
          onClear={resetFilters}
          isLoading={isFetching}
        >
          <div className="relative">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search branches..."
              value={globalFilter}
              onChange={(e) => setGlobalFilter(e.target.value)}
              className="pl-8 max-w-sm font-light"
            />
          </div>
          {/* Active Status Filter */}
          <SelectFilter
            placeholder="Status"
            options={[
              { label: "Active", value: "true" },
              { label: "Inactive", value: "false" },
            ]}
            value={activeFilter}
            onChange={(value) => {
              if (value === "clear") {
                setActiveFilter(undefined);
              } else {
                setActiveFilter(value);
              }
            }}
          />
        </FiltersPanel>

        {/* Branches Table */}
        <BranchTable
          branches={branches}
          onView={handleView}
          onEdit={handleEdit}
          onDelete={handleDeleteClick}
          isLoading={isLoading}
          ordering={ordering}
          canManage={canManage}
          onOrderingChange={toggleOrdering}
        />

        <PaginationComponent
          count={count}
          page={page}
          onPageChange={handlePageChange}
          isLoading={isFetching}
          pageSize={pageSize}
          onPageSizeChange={handlePageSizeChange}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
        />

        {/* Branch Form Sheet */}
        <BranchFormSheet
          isOpen={isSheetOpen}
          onClose={() => setIsSheetOpen(false)}
          onSubmit={handleFormSubmit}
          editingBranch={editingBranch}
          isViewOnly={isViewOnly}
          isLoading={isMutating}
        />

        {/* Delete Confirmation */}
        <DeleteConfirmDialog
          isOpen={isDeleteDialogOpen}
          onClose={() => setIsDeleteDialogOpen(false)}
          onConfirm={handleDeleteConfirm}
          title="Delete Branch"
          description={`Are you sure you want to delete branch "${branchToDelete?.name}"? This action cannot be undone.`}
          itemName={branchToDelete?.name}
          isLoading={deleteBranch.isPending}
          destructive
        />
      </div>
    </SidebarInset>
  );
}
